import {
    PanelSnapshot,
    SellerConfigPatch,
    TeamCreatePayload,
    TeamUpdatePatch,
    VipAssignByEmailPayload,
    VipOverridePatch,
} from './panel_comerciales.types';

const BASE_URL = process.env.REACT_APP_API_CRM_PANEL ?? '';
const API_KEY = process.env.REACT_APP_XAPIKEY_CRM_PANEL ?? '';

export function currentPeriod(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

interface ApiResponse<T = unknown> {
    success: boolean;
    code: string;
    message?: string;
    data: T;
}

function headers(token: string): Record<string, string> {
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-api-key': API_KEY,
    };
}

async function unwrap<T>(res: Response): Promise<T> {
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text}`);
    }
    const json: ApiResponse<T> = await res.json();
    if (!json.success) {
        throw new Error(json.message ?? json.code ?? 'Error del servidor');
    }
    return json.data;
}

/**
 * Etiquetas legibles del catálogo de PRODUCTOS (`pipedrive_products`).
 * Las claves son `product_code`.
 */
export const PRODUCT_LABELS: Record<string, string> = {
    DESTACADO: 'Destacado',
    PRIME: 'Prime',
    ELITE: 'Elite',
    SMART_LEADS: 'Smart Leads',
    ANUNCIO_SIMPLE: 'Anuncio Simple',
    OPORTUNIDADES_ILIMITADAS: 'Oportunidades ilimitadas',
};

/**
 * Etiquetas legibles del catálogo de TIPOS DE FLUJO. Las claves son
 * `pipeline_key` (catálogo `pipedrive_pipeline` agrupado por
 * `pipedrive_scoring_flow_config.pipeline_key`).
 */
export const FLOW_TYPE_LABELS: Record<string, string> = {
    RENOVACION: 'Renovación',
    NUEVO_CLIENTE: 'Nuevo cliente',
    CARRITO: 'Carrito',
    RECUPERACION: 'Recuperación',
    INTERES_FUTURO: 'Interés futuro',
};

/**
 * Catálogo de flujos por defecto (orden de presentación) usado como fallback
 * cuando el backend aún no envía `meta.flowTypes`.
 */
export const DEFAULT_FLOW_TYPES: { key: string; label: string }[] = [
    'RENOVACION',
    'NUEVO_CLIENTE',
    'CARRITO',
    'RECUPERACION',
    'INTERES_FUTURO',
].map((k) => ({ key: k, label: FLOW_TYPE_LABELS[k] }));

type CatalogEntry = { key: string; label: string };

/** Construye un catálogo a partir de las claves de los dicts de comerciales. */
function inferCatalog(
    keysByCommercial: Record<string, number>[],
    labels: Record<string, string>,
): CatalogEntry[] {
    const seen = new Set<string>();
    keysByCommercial.forEach((dict) =>
        Object.keys(dict).forEach((k) => seen.add(k)),
    );
    return Array.from(seen).map((k) => ({ key: k, label: labels[k] ?? k }));
}

/**
 * Normaliza el snapshot a los DOS EJES de distribución (producto y flujo):
 *  - `meta.products` (eje PRODUCTO) y `meta.flowTypes` (eje FLUJO) son fuentes
 *    canónicas e independientes. Si el backend no las envía, se infieren de las
 *    claves de los dicts de los comerciales; el eje flujo cae a
 *    `DEFAULT_FLOW_TYPES`.
 *  - en cada comercial: `products` y `flows` quedan poblados (al menos `{}`) y
 *    `specialty` se mantiene como alias del eje producto (retrocompat).
 */
function inferProducts(snapshot: PanelSnapshot): PanelSnapshot {
    const commercials = snapshot.commercials.map((c) => {
        const productDict = c.products ?? c.specialty ?? {};
        const flowDict = c.flows ?? {};
        return { ...c, products: productDict, specialty: productDict, flows: flowDict };
    });

    let productCatalog: CatalogEntry[] | undefined = snapshot.meta.products?.length
        ? snapshot.meta.products
        : undefined;
    if (!productCatalog?.length) {
        const inferred = inferCatalog(
            commercials.map((c) => c.products ?? {}),
            PRODUCT_LABELS,
        );
        productCatalog = inferred;
    }

    let flowCatalog: CatalogEntry[] | undefined = snapshot.meta.flowTypes?.length
        ? snapshot.meta.flowTypes
        : undefined;
    if (!flowCatalog?.length) {
        const inferred = inferCatalog(
            commercials.map((c) => c.flows ?? {}),
            FLOW_TYPE_LABELS,
        );
        flowCatalog = inferred.length ? inferred : DEFAULT_FLOW_TYPES;
    }

    return {
        ...snapshot,
        meta: { ...snapshot.meta, products: productCatalog, flowTypes: flowCatalog },
        commercials,
    };
}

export const getSnapshot = async (
    token: string,
    period: string = currentPeriod(),
): Promise<PanelSnapshot> => {
    const url = `${BASE_URL}commercial-panel/snapshot?period=${period}&country=MX`;
    const res = await fetch(url, { method: 'GET', headers: headers(token) });
    const snapshot = await unwrap<PanelSnapshot>(res);
    return inferProducts(snapshot);
};

export const saveSellerConfig = async (
    token: string,
    sellerId: number,
    patch: SellerConfigPatch,
): Promise<void> => {
    const url = `${BASE_URL}commercial-panel/sellers/${sellerId}/config?country=MX`;
    const body = { ...patch, period: patch.period ?? currentPeriod() };
    const res = await fetch(url, {
        method: 'PUT',
        headers: headers(token),
        body: JSON.stringify(body),
    });
    await unwrap(res);
};

export const saveVipOverride = async (
    token: string,
    clientId: string,
    patch: VipOverridePatch,
): Promise<void> => {
    const url = `${BASE_URL}commercial-panel/clients/${clientId}/vip?country=MX`;
    const res = await fetch(url, {
        method: 'PUT',
        headers: headers(token),
        body: JSON.stringify(patch),
    });
    await unwrap(res);
};

export const assignVipByEmail = async (
    token: string,
    payload: VipAssignByEmailPayload,
): Promise<void> => {
    const url = `${BASE_URL}commercial-panel/clients/vip?country=MX`;
    const res = await fetch(url, {
        method: 'POST',
        headers: headers(token),
        body: JSON.stringify(payload),
    });
    await unwrap(res);
};

export const createTeam = async (
    token: string,
    payload: TeamCreatePayload,
): Promise<void> => {
    const url = `${BASE_URL}commercial-panel/teams?country=MX`;
    const res = await fetch(url, {
        method: 'POST',
        headers: headers(token),
        body: JSON.stringify(payload),
    });
    await unwrap(res);
};

export const updateTeam = async (
    token: string,
    teamId: number,
    patch: TeamUpdatePatch,
): Promise<void> => {
    const url = `${BASE_URL}commercial-panel/teams/${teamId}?country=MX`;
    const res = await fetch(url, {
        method: 'PUT',
        headers: headers(token),
        body: JSON.stringify(patch),
    });
    await unwrap(res);
};
